/**
 * Beat 12's queue and following string, expressed only in lane metres.
 *
 * The forty are deliberately one scalar per member, not agents looking for a
 * route: the race already is their route. Keeping coordinates out of this
 * module means a fixed-step trace can prove the queue before Three, Rapier, or
 * a room exists, and prevents a second approximation of the yard lane from
 * changing who gets through.
 */
export const DAWN_HERD_SIZE = 40;
/** One body takes this long through Holly's held leaf: forty make the planned minute of pressure. */
export const GATE_SERVICE_SECONDS = 1.5;
/**
 * Nose to nose along the lane. A finisher is 1.15 m long, so beat 10's 1.1
 * overlapped them: the first close frame was a train of boxes, not animals.
 * 1.5 leaves a body's width of daylight between each. It also bounds how many
 * bodies the closing lane can honestly hold.
 */
export const HERD_SPACING = 1.5;
/** A walking lead must feel like she is carrying a weight, not dragging a frozen prop. */
export const HERD_WALK_SPEED = 1.3;
/** A roll improves the opening, but is capped below a sprint so the string remains legible. */
export const HERD_TROT_SPEED = 3;
/** The head follows just behind Rosalie, leaving her visible ahead of the first animal. */
export const FOLLOW_GAP = 1.2;
/** Farther than this, the string has lost its leader instead of gaining free remote control. */
export const DRAW_RANGE = 10;
/** The lane is a broad yard corridor; smaller would turn a dodge into a hidden failure. */
export const LEAD_OFFSET_LIMIT = 4;
const RACE_VISIBLE_DEPTH = 13.6;
/** The twelve bodies visible in the race; the remaining crowd is shed haze. */
export const DAWN_HERD_RACE_COUNT = Math.floor((RACE_VISIBLE_DEPTH - 0.6) / HERD_SPACING) + 1;

export type DawnHerdMemberState = 'shed' | 'race' | 'out';
export interface DawnHerdOptions { readonly laneLength: number; readonly gateDistance: number; }
export interface DawnHerdLead { readonly distance: number; readonly offset: number; }
export interface DawnHerdInput { readonly released: boolean; readonly lead: DawnHerdLead; readonly leadSpeed: number; }
export interface DawnHerdMember { readonly index: number; readonly state: DawnHerdMemberState; readonly distance: number; }
export interface DawnHerdSnapshot {
  readonly members: readonly DawnHerdMember[];
  readonly headDistance: number | null;
  readonly throughs: number;
  readonly atVehicleGate: boolean;
  readonly drawn: boolean;
}

/**
 * One service slot through Holly's held leaf feeds the string.
 *
 * `throughs` is monotonic because `out` is a presentation state: a snapshot
 * taken while an animal is in the yard cannot say whether the queue has ever
 * begun. The reset gate replays after a partial service interval, which is the
 * hidden timer state a construction-only equality check would miss.
 */
export class DawnHerd {
  readonly #options: DawnHerdOptions;
  readonly #laneCapacity: number;
  readonly #distances = Array.from({ length: DAWN_HERD_SIZE }, (_, index) => -0.6 - index * HERD_SPACING);
  #throughs = 0;
  #service = 0;
  #atVehicleGate = false;
  #drawn = false;

  constructor(options: DawnHerdOptions) {
    if (options.laneLength <= 0 || options.gateDistance <= 0 || options.gateDistance > options.laneLength) {
      throw new Error('dawn herd: lane and gate distances must be positive and ordered');
    }
    this.#options = options;
    this.#laneCapacity = Math.floor(options.laneLength / HERD_SPACING) + 1;
  }

  step(dt: number, input: DawnHerdInput): void {
    if (dt < 0) throw new Error('dawn herd: dt must not be negative');
    const outMoved = this.#advanceOut(dt, input);
    const raceMoved = this.#advanceRace(dt);
    this.#serve(dt, input.released);
    this.#drawn = outMoved || raceMoved;
  }

  #advanceOut(dt: number, input: DawnHerdInput): boolean {
    // Once the head has reached the vehicle gate, the close keeps streaming at
    // a walk. A held roll input must not make an off-screen tail accelerate.
    const speed = this.#atVehicleGate ? HERD_WALK_SPEED : Math.min(HERD_TROT_SPEED, Math.max(HERD_WALK_SPEED, input.leadSpeed));
    let moved = false;
    for (let index = 0; index < this.#throughs; index += 1) {
      const current = this.#distances[index] ?? 0;
      let target: number;
      if (this.#atVehicleGate) target = Math.max(0, this.#options.laneLength - index * HERD_SPACING);
      else if (index === 0) {
        const withinLead = input.lead.offset <= LEAD_OFFSET_LIMIT && input.lead.distance - current <= DRAW_RANGE;
        target = withinLead ? Math.max(current, input.lead.distance - FOLLOW_GAP) : current;
      } else target = Math.max(current, (this.#distances[index - 1] ?? current) - HERD_SPACING);
      const next = Math.min(target, current + speed * dt);
      moved ||= next > current + 0.000001;
      this.#distances[index] = next;
    }
    if (this.#throughs > 0 && (this.#distances[0] ?? -Infinity) >= this.#options.gateDistance) this.#atVehicleGate = true;
    return moved;
  }

  #advanceRace(dt: number): boolean {
    let moved = false;
    for (let index = this.#throughs; index < DAWN_HERD_SIZE; index += 1) {
      const current = this.#distances[index] ?? -Infinity;
      // The front animal walks TO the mouth. Every following animal inherits
      // the newly freed slot one spacing behind it; subtracting from zero here
      // would make the front back away from the leaf forever.
      const target = index === this.#throughs ? 0 : Math.min(0, (this.#distances[index - 1] ?? -Infinity) - HERD_SPACING);
      if (target > current) {
        const next = Math.min(target, current + HERD_WALK_SPEED * dt);
        moved ||= next > current + 0.000001;
        this.#distances[index] = next;
      }
    }
    return moved;
  }

  #serve(dt: number, throatOpen: boolean): void {
    // The close only has room for a finite, spaced stream. Leave the remaining
    // animals queued in the race instead of collapsing them at the gate; the
    // beat carries no count and stage 13 inherits a crowd, not an overlap.
    if (this.#atVehicleGate && this.#throughs >= this.#laneCapacity) return;
    // This is the interval SINCE the previous release, rather than a dwell
    // that starts after the next pig reaches the mouth. The race advances while
    // it runs; otherwise 1.1 m of walking is silently added to every 1.5 s
    // service slot and the plan's one-at-a-time cadence turns into 2.35 s.
    this.#service += dt;
    if (!throatOpen) return;
    const front = this.#distances[this.#throughs];
    const ahead = this.#throughs === 0 ? Infinity : this.#distances[this.#throughs - 1] ?? -Infinity;
    if (front === undefined || front < -0.0001 || ahead < HERD_SPACING) return;
    if (this.#service < GATE_SERVICE_SECONDS) return;
    this.#service = 0;
    this.#distances[this.#throughs] = 0;
    this.#throughs += 1;
  }

  reset(): void {
    for (let index = 0; index < DAWN_HERD_SIZE; index += 1) this.#distances[index] = -0.6 - index * HERD_SPACING;
    this.#throughs = 0;
    this.#service = 0;
    this.#atVehicleGate = false;
    this.#drawn = false;
  }

  snapshot(): DawnHerdSnapshot {
    const members = this.#distances.map((distance, index): DawnHerdMember => ({
      index, distance, state: index < this.#throughs ? 'out' : distance >= -RACE_VISIBLE_DEPTH ? 'race' : 'shed',
    }));
    return { members, headDistance: this.#throughs === 0 ? null : this.#distances[0] ?? null, throughs: this.#throughs, atVehicleGate: this.#atVehicleGate, drawn: this.#drawn };
  }
}
